import { performWithRetries } from './performWithRetries';

export interface RetryConfig {
  enabled?: boolean;
  maxRetries?: number;
  initialDelayMs?: number;
}

export interface RetryableFetchInit extends RequestInit {
  retry?: RetryConfig;
}

export interface RetryableFetchConfig {
  maxRetries?: number;
  initialDelayMs?: number;
  retryMethods?: string[];
  retryStatusCodes?: number[];
  fetch?: typeof fetch;
}

type RetryableFetchFn = (
  input: RequestInfo | URL,
  init?: RetryableFetchInit,
) => Promise<Response>;

class RetryableError extends Error {
  constructor(
    message: string,
    public readonly response?: Response,
  ) {
    super(message);
    this.name = 'RetryableError';
  }
}

const shouldRetryRequest = (
  method: string,
  retryMethods: string[],
  retryConfig?: RetryConfig,
): boolean => {
  if (retryConfig?.enabled === false) {
    return false;
  }

  if (retryConfig?.enabled === true) {
    return true;
  }

  return retryMethods.includes(method.toUpperCase());
};

export const createRetryableFetch = (
  config: RetryableFetchConfig = {},
): RetryableFetchFn => {
  const {
    maxRetries = 3,
    initialDelayMs = 1000,
    retryMethods = ['GET', 'HEAD', 'OPTIONS'],
    retryStatusCodes = [429, 500, 502, 503, 504],
    fetch: fetchFn = fetch,
  } = config;

  return async (
    input: RequestInfo | URL,
    init?: RetryableFetchInit,
  ): Promise<Response> => {
    const method = init?.method || 'GET';
    const retryConfig = init?.retry;
    const effectiveMaxRetries = retryConfig?.maxRetries ?? maxRetries;
    const effectiveInitialDelay = retryConfig?.initialDelayMs ?? initialDelayMs;

    const shouldRetry = shouldRetryRequest(method, retryMethods, retryConfig);

    if (!shouldRetry) {
      return fetchFn(input, init);
    }

    return performWithRetries(
      async () => {
        const response = await fetchFn(input, init);

        if (!response.ok && retryStatusCodes.includes(response.status)) {
          throw new RetryableError(
            `Request failed with status ${response.status}`,
            response,
          );
        }

        return response;
      },
      {
        retryCount: effectiveMaxRetries + 1,
        retryDelayMs: effectiveInitialDelay,
        exponentialBackoff: true,
      },
    ).catch((error) => {
      if (error instanceof RetryableError && error.response) {
        return error.response;
      }

      throw error;
    });
  };
};

export const retryableFetch = createRetryableFetch();
